import React, { useState } from 'react';
import './DetailsModal.css';

// Компонент для отображения роли
const RoleCard = ({ role, index }) => (
  <div className="role-card">
    <div className="role-header">
      <span className="role-index">#{index + 1}</span>
      <span className="role-type">{role.role}</span>
    </div>
    <div className="role-content">
      <div className="role-field">
        <span className="field-label">Лемма:</span>
        <span className="field-value">{role.lemma || 'Нет данных'}</span>
      </div>
      <div className="role-field">
        <span className="field-label">Текст:</span>
        <span className="field-value">{role.text || 'Нет данных'}</span>
      </div>
      <div className="role-field">
        <span className="field-label">Уверенность:</span>
        <span className="field-value score">
          {role.score ? `${(role.score * 100).toFixed(1)}%` : 'Нет данных'}
        </span>
      </div>
    </div>
  </div>
);

// Компонент для отображения элемента анализа
const AnalysisItem = ({ item, index }) => (
  <div className="analysis-item">
    <div className="item-header">
      <h4 className="item-title">Элемент анализа #{index + 1}</h4>
    </div>
    <div className="item-content">
      {item.marked_text && (
        <div className="item-field">
          <span className="field-label">Отмеченный текст:</span>
          <span className="field-value">{item.marked_text}</span>
        </div>
      )}
      
      {item.roles && item.roles.length > 0 && (
        <div className="roles-section">
          <h5 className="section-title">Роли ({item.roles.length})</h5>
          <div className="roles-grid">
            {item.roles.map((role, roleIndex) => (
              <RoleCard key={roleIndex} role={role} index={roleIndex} />
            ))}
          </div>
        </div>
      )}
    </div>
  </div>
);

// Функция для отображения данных в структурированном виде
const renderStructuredData = (data) => {
  // Если это массив, обрабатываем каждый элемент
  if (Array.isArray(data)) {
    return (
      <div className="structured-data">
        <div className="data-header">
          <h3 className="data-title">Результаты анализа</h3>
          <span className="data-count">Всего элементов: {data.length}</span>
        </div>
        <div className="analysis-items">
          {data.map((item, index) => (
            <AnalysisItem key={index} item={item} index={index} />
          ))}
        </div>
      </div>
    );
  }
  
  // Если это объект, отображаем его поля
  if (typeof data === 'object' && data !== null) {
    return (
      <div className="structured-data">
        <div className="data-header">
          <h3 className="data-title">Данные анализа</h3>
        </div>
        <div className="object-fields">
          {Object.entries(data).map(([key, value]) => (
            <div key={key} className="object-field">
              <span className="field-label">{key}:</span>
              <span className="field-value">
                {typeof value === 'object' 
                  ? JSON.stringify(value, null, 2)
                  : String(value)
                }
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  }
  
  // Для примитивных значений
  return (
    <div className="structured-data">
      <div className="primitive-value">
        {String(data)}
      </div>
    </div>
  );
};

// Функция для отображения оригинального JSON с подсветкой
const renderOriginalJson = (data) => {
  const jsonString = JSON.stringify(data, null, 2);
  
  // Разбиваем JSON на строки и обрабатываем каждую
  const lines = jsonString.split('\n');
  
  return (
    <div className="json-viewer">
      <div className="json-header">
        <h3 className="json-title">Оригинальный JSON</h3>
        <span className="json-size">{jsonString.length} символов</span>
      </div>
      <div className="json-content">
        {lines.map((line, index) => {
          // Определяем тип строки для подсветки
          let className = 'json-line';
          
          if (line.trim().startsWith('"')) {
            // Ключи объектов
            if (line.includes('":')) {
              className += ' json-key';
            } else {
              // Строковые значения
              className += ' json-string';
            }
          } else if (line.trim().match(/^-?\d+\.?\d*$/)) {
            // Числовые значения
            className += ' json-number';
          } else if (line.trim() === 'true' || line.trim() === 'false') {
            // Булевы значения
            className += ' json-boolean';
          } else if (line.trim() === 'null') {
            // Null значения
            className += ' json-null';
          } else if (line.trim() === '{' || line.trim() === '}') {
            // Скобки объектов
            className += ' json-brace';
          } else if (line.trim() === '[' || line.trim() === ']') {
            // Скобки массивов
            className += ' json-bracket';
          } else if (line.trim().endsWith(',')) {
            // Запятые
            className += ' json-comma';
          } else if (line.trim() === '') {
            // Пустые строки
            className += ' json-empty';
          }
          
          return (
            <div key={index} className={className}>
              {line}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const JsonModal = ({ isOpen, onClose, data, title = "Полный анализ" }) => {
  const [activeTab, setActiveTab] = useState('structured'); // 'structured' или 'json'

  if (!isOpen) return null;

  return (
    <div className="json-modal-overlay" onClick={onClose}>
      <div className="json-modal" onClick={(e) => e.stopPropagation()}>
        <div className="json-modal-header">
          <h3 className="json-modal-title">{title}</h3>
          <button
            onClick={onClose}
            className="json-modal-close"
            aria-label="Закрыть"
          >
            ×
          </button>
        </div>
        
        {/* Вкладки */}
        <div className="modal-tabs">
          <button
            className={`modal-tab ${activeTab === 'structured' ? 'active' : ''}`}
            onClick={() => setActiveTab('structured')}
          >
            Структурированный вид
          </button>
          <button
            className={`modal-tab ${activeTab === 'json' ? 'active' : ''}`}
            onClick={() => setActiveTab('json')}
          >
            Оригинальный JSON
          </button>
        </div>
        
        <div className="json-modal-content">
          {activeTab === 'structured' ? renderStructuredData(data) : renderOriginalJson(data)}
        </div>
        
        <div className="json-modal-footer">
          <button
            onClick={onClose}
            className="json-modal-button"
          >
            Закрыть
          </button>
        </div>
      </div>
    </div>
  );
};

export default JsonModal; 